import { ErrorView } from '@aibees/shared/views/error';
import posthog from 'posthog-js';
import React, { ErrorInfo } from 'react';

type DetectedOS = 'macos' | 'windows' | 'linux' | 'other';

function getOS(): DetectedOS {
  if (typeof navigator === 'undefined') return 'other';
  const platform = navigator.platform?.toLowerCase() ?? '';
  const ua = navigator.userAgent?.toLowerCase() ?? '';
  if (platform.includes('mac') || ua.includes('mac')) return 'macos';
  if (platform.includes('win') || ua.includes('windows')) return 'windows';
  if (platform.includes('linux') || ua.includes('linux')) return 'linux';
  return 'other';
}

function getReloadInstruction(hasScheduledAutoReload: boolean): string {
  const os = getOS();
  const prefix = hasScheduledAutoReload
    ? 'Instead of waiting for the automatic reload, '
    : '';

  switch (os) {
    case 'macos':
      return `${prefix}Please press Command + Shift + R to reload now.`;
    case 'windows':
    case 'linux':
      return `${prefix}Please press Ctrl + Shift + R to reload now.`;
    default:
      return `${prefix}Please reload the page or use the button below.`;
  }
}

class ErrorBoundary<T = any> extends React.Component<
  { children: React.ReactNode },
  T
> {
  private hasScheduledAutoReload = false;

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {} as T;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error, errorInfo);
    this.setState({ error } as any);

    posthog.capture('error_boundary_caught', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    if (
      error.message.includes('Failed to fetch') &&
      !this.hasScheduledAutoReload
    ) {
      this.hasScheduledAutoReload = true;
      setTimeout(() => this.eraseCache(), 1000);
    }
  }

  eraseCache = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_ts', Date.now().toString());
      window.location.href = url.toString();
    } catch {
      // Fallback if URL construction fails for any reason
      window.location.reload();
    }
  };

  render(): React.ReactNode {
    if ((this.state as any).error) {
      const error = this.state as any;
      const isFailedToFetch = error.error.message.includes('Failed to fetch');
      const description = isFailedToFetch
        ? `A new version of the app is available and the page will reload automatically in a moment. ${getReloadInstruction(
            this.hasScheduledAutoReload
          )}`
        : error.error.message;

      return (
        <ErrorView
          title={
            isFailedToFetch
              ? 'Reloading to get the latest version'
              : 'Something went wrong!'
          }
          description={description}
          onRedirect={() =>
            isFailedToFetch
              ? this.eraseCache()
              : this.setState({ error: null } as any)
          }
          redirectToTitle={isFailedToFetch ? 'Reload now' : 'Go to Homepage'}
        />
      );
    }

    return (this.props as any).children;
  }
}

export default ErrorBoundary;
